import { Box, Stack, Typography } from "@mui/material"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { AuthoringAuthorNoteBox } from "./authoring-author-note-box"
import { AuthoringAmendsDecisionBanner } from "./authoring-amends-decision-banner"
import { normaliseAuthoringAmendsAuditPayload } from "../amends/lib/normalise-authoring-amends-audit-frequency"
import { createSlot } from "lib/@components/slot/create-slot"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { ChangeTitleRow } from "./change-title-row"
import { AuthoringAudit } from "./authoring-audit"
import { PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { hasLongContent } from "authoring/routes/project/amends/lib/amends-content-preview"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import { LEADING_SUMMARY_AMENDS_KEY } from "authoring/routes/project/amends/lib/constants/amends-keys"

export const AuthoringAuthorNotesEntryAction = createSlot("Authoring.AuthorNotesEntryAction")
const LEGISLATION_PAYLOAD_KEYS = new Set(["legislationId", "legislationHTML", "legislation"])

export function AuthoringAuthorNotesEntry({ item: entry }) {
    const amend = entry?.primaryAmend
    const payload = amend?.payload ?? {}
    const taskTitle = getTaskTitle()
    const payloadWithTaskTitle = taskTitle && taskTitle !== payload?.taskTitle ? { ...payload, taskTitle } : payload
    const displayPayload = normaliseAuthoringAmendsAuditPayload(payloadWithTaskTitle)
    const dateLabel = amend?.date ? new Date(amend.date).format(DATE_FORMATS.long) : ""
    const original = sanitiseAuditValues(displayPayload?.original)
    const tailored = sanitiseAuditValues({
        ...displayPayload?.tailored,
        changeType: displayPayload?.changeType,
    })
    const auditKeys = buildAuditKeys(displayPayload)
    const showComparison = auditKeys.length > 0

    return (
        <Box
            className="audit-comment"
            sx={{ my: 2, display: "flex", minWidth: "100%", pb: 3 }}
            data-cy="authoring-author-note-entry"
        >
            <Box sx={{ m: 1, mr: 2 }}>
                <UserAvatar sx={{ width: 32, height: 32 }} user={{ displayName: amend?.userName }} />
            </Box>
            <Box sx={{ minWidth: "80%" }}>
                <Box display="flex" alignItems="center" sx={{ my: 1 }}>
                    <TruncatedTypography
                        variant="caption"
                        sx={{ color: "text.secondary", maxWidth: "fit-content", flex: 1 }}
                    >
                        {amend?.userName}
                    </TruncatedTypography>
                    <Typography variant={"caption"} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        , {dateLabel}
                    </Typography>
                </Box>
                <If
                    truthy={showComparison}
                    then={
                        <AuthoringAudit
                            taskTitle={taskTitle}
                            auditKeys={auditKeys}
                            original={original}
                            tailored={tailored}
                            displayPayload={displayPayload}
                            entry={entry}
                        />
                    }
                    else={
                        <ChangeTitleRow
                            taskTitle={taskTitle}
                            displayPayload={displayPayload}
                            original={original}
                            tailored={tailored}
                            entry={entry}
                        />
                    }
                />
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Repeat list={entry?.amends ?? []} item={<AuthoringAuthorNoteBox />} />
                    <If truthy={!!entry?.decision}>
                        <AuthoringAmendsDecisionBanner decision={entry?.decision} {...getDecisionBannerProps(entry)} />
                    </If>
                </Stack>
            </Box>
        </Box>
    )

    function buildAuditKeys(payload = {}) {
        const tailoredEntries = Object.entries(payload?.tailored ?? {})
            .filter(([, value]) => value != null)
            .filter(([key]) => !LEGISLATION_PAYLOAD_KEYS.has(key))
            .map(([key]) => key)
        if (payload?.changeType === AmendsChangeType.ADDED || payload?.changeType === AmendsChangeType.REMOVED) {
            return ["changeType", ...tailoredEntries.filter((key) => key !== "changeType")]
        }
        return tailoredEntries
    }

    function sanitiseAuditValues(values = {}) {
        const safeValues = values && typeof values === "object" ? values : {}
        const output = {}

        for (const [key, value] of Object.entries(safeValues)) {
            if (value == null) continue
            output[key] = value
        }

        const amendsKey = payload?.amendsKey ?? ""
        if (!output.skill && !output.skillSet && !amendsKey.startsWith("legislation.")) {
            output.skillSet = "1"
            output.skill = {
                CoreSkillingID: "1",
                Skilling: "Unknown",
                SkillingCode: "??",
            }
        }

        return output
    }

    function getTaskTitle() {
        if (payload?.amendsKey === LEADING_SUMMARY_AMENDS_KEY || payload?.field === LEADING_SUMMARY_AMENDS_KEY) {
            return "Leading Summary"
        }

        return entry?.displayTaskTitle ?? payload?.taskTitle
    }

    function getDecisionBannerProps(entry) {
        if (!isTaskEntry(entry) && !isLeadingSummaryEntry(entry)) return {}
        const reason = entry?.decision?.payload?.reason ?? ""
        const rejected = entry?.decision?.payload?.status === PAYLOAD_STATUS_REJECTED
        return {
            showReasonDetailsAction: rejected && hasLongContent(reason),
        }
    }

    function isTaskEntry(entry) {
        return (entry?.key ?? "").startsWith("task:")
    }

    function isLeadingSummaryEntry(entry) {
        return entry?.key === LEADING_SUMMARY_AMENDS_KEY
    }
}
